import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Lock, ArrowLeft } from "lucide-react";

const errText = (d, f) => (typeof d === "string" ? d : f);

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("loading"); // loading | login | forgot
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api
      .get("/auth/setup-status")
      .then((r) => {
        if (r.data.needs_setup) {
          navigate("/admin/setup", { replace: true });
        } else {
          setMode("login");
        }
      })
      .catch(() => setMode("login"));
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const u = await login(form.email, form.password);
        navigate(u.role === "client" ? "/portal" : "/admin");
      } else if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        toast.success("If that email exists, a reset link has been sent.");
        setMode("login");
      }
    } catch (err) {
      toast.error(errText(err.response?.data?.detail, "Something went wrong"));
    } finally {
      setLoading(false);
    }
  };

  if (mode === "loading") {
    return <div className="min-h-screen bg-[#050505] flex items-center justify-center text-gold font-serif text-xl">…</div>;
  }

  const isForgot = mode === "forgot";

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505] px-6">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-3 mb-3">
          <Lock className="text-gold" />
          <span className="font-serif text-2xl">
            <span className="gold-gradient-text">GI</span> <span className="silver-gradient-text">Admin</span>
          </span>
        </div>
        <h1 className="font-serif text-3xl mb-2 text-white">
          {isForgot ? "Reset your password" : "Sign in"}
        </h1>
        <p className="text-white/50 font-body text-sm mb-8">
          {isForgot ? "Enter your email and we'll send you a reset link." : "Access the GI Remodeling admin panel."}
        </p>

        <form data-testid="login-form" onSubmit={submit} className="space-y-6">
          <input data-testid="login-email" type="email" required placeholder="Email" value={form.email} onChange={set("email")} className="editorial-input" />
          {!isForgot && (
            <input data-testid="login-password" type="password" required placeholder="Password" value={form.password} onChange={set("password")} className="editorial-input" />
          )}
          <button data-testid="login-submit" disabled={loading} className="w-full bg-gold text-black text-sm font-semibold uppercase tracking-widest py-4 hover:bg-white transition-colors disabled:opacity-50">
            {loading ? "..." : isForgot ? "Send reset link" : "Sign In"}
          </button>
        </form>

        <div className="mt-6 flex items-center justify-between text-sm">
          {isForgot ? (
            <button onClick={() => setMode("login")} className="text-white/60 hover:text-gold flex items-center gap-2"><ArrowLeft size={14} /> Back to sign in</button>
          ) : (
            <button data-testid="forgot-link" onClick={() => setMode("forgot")} className="text-white/60 hover:text-gold link-underline">Forgot password?</button>
          )}
        </div>
      </div>
    </div>
  );
}
